import {
  CREAutoFillChatPayload,
  FileCREProjectData,
  FileCREProjectPollingData,
  NewAnalysisInvestmentProjectData,
} from '@/app/store/new-investment-analysis-store';
import { ApprovedModel } from '@/hooks/mutate/use-transaction';
import {
  azureBlobServer,
  server,
  serverCreApi,
  serverDiana,
  serverDianaApi,
  serverExo,
} from '@/lib/axios-util';
import { FormRequestDemoValue, FormSignUpValue } from '@/types/auth';
import {
  OnboardingDianaChatItem,
  ValidateDianaOnboardingCSV,
} from '@/types/diana-onboarding-type';
import { EuclidCreditAnalysisSchema } from '@/types/euclid-credit-analysis';
import {
  analyzedDocs,
  BatchProjectModelTest,
  BlacklistInitiate,
  BlacklistValueModel,
  BulkInviteUserPayload,
  BulkUploadData,
  deleteInviteePayload,
  DianaMessageMode,
  docCheckerAnalysis,
  EditInputsParams,
  EscalateModel,
  ExportCsvModel,
  GenerateReportModel,
  GetAnalyzedDocsParams,
  GetTransactionInsightModel,
  IAssignCase,
  inviteInvestorPayload,
  NotificationModel,
  OrganizationModel,
  projectTags,
  requestNewLinkPayload,
  RulesModel,
  ScenariosMessageMode,
  SettingsFRoleModel,
  TeamMemberModel,
  TestRuleModel,
  TransactionNoteModel,
  updateInviteeNamePayload,
  updateInviteePayload,
  UpdateScreeningSettingsModel,
  WebhookPostData,
} from '@/types/general';
import { AxiosRequestConfig } from 'axios';
import { Session } from 'next-auth';
import qs from 'qs';

const apis = {
  auth: {
    user: {
      forgetPassword: (data: { email: string }) =>
        serverExo.post('/user/forgot-password', data),
      resetPassword: (data: { password: string; token: string }) =>
        serverExo.post(
          '/user/reset-password',
          { password: data.password },
          { headers: { Authorization: `Bearer ${data.token}` } }
        ),
      updateProfile: (data: Session['user']) =>
        serverExo.put('/user/update-profile', data),
      getUserProfile: () => serverExo.get('/user/profile'),
      changePassword: (data: { oldPassword: string; newPassword: string }) =>
        serverExo.put('/user/update-password', data),
      sendRegistrationOtp: (data: { email: string; firstName: string }) =>
        serverExo.post('/user/send-email-otp', data),
      register: (data: FormSignUpValue) =>
        serverExo.post('/user/register', data),
    },
  },
  organization: {
    updateOrganization: (data: OrganizationModel) =>
      serverExo.put(`/user/update-organization`, data),
    getOrganization: () =>
      serverExo.get<OrganizationModel>('/user/organization'),
  },
  notification: {
    updateNotification: (data: NotificationModel) =>
      server.put(`/user/update-notification-settings`, data),
  },
  blacklist: {
    initiate: (data: BlacklistInitiate) =>
      server.post('/blacklist/bulk-upload/initiate', data),
    uploadToAzureBlob: (data: { url: string; file: File }) =>
      azureBlobServer.put(data?.url, data.file, {
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': data.file.type,
        },
      }),
    bulkUpload: (data: BulkUploadData) =>
      server.post('/blacklist/bulk-upload', data),
    deleteBlacklist: (url: string) => server.delete(url),
    createBlacklist: (data: BlacklistValueModel) =>
      server.post('/blacklist/black-list', data),
    getBlacklistTransaction: (id: string) =>
      server.get(`/blacklist/transaction-state/${id}`),
    getBlacklist: (url: string) => server.get(url),
    getSingleBlacklist: (url: string) => server.get(url),
  },
  apiKeys: {
    getApiKeys: (query?: string) => server.get(`/api-key${query}`),
    generateApiKey: () => server.post('/api-key/generate'),
    revokeApiKey: (id: string) => server.delete(`/api-key/${id}`),
  },
  webhooks: {
    getWebhook: () => server.get('/webhook'),
    addWebhook: (data: WebhookPostData) => server.post('/webhook', data),
    disableWebhook: () => server.delete('/webhook'),
    generateNewWebhookKey: () => server.get('/webhook/key'),
  },
  fibonacci: {
    getInsightExplainer: (id: string) =>
      server.get(`/fibonacci/transactions/${id}/insight-explainer`),
    exportCsv: (data: ExportCsvModel) =>
      server.post('/fibonacci/export-csv', data),
    getTransactionInsight: (data: GetTransactionInsightModel) =>
      server.post('/fibonacci/organization/transactions/insights', data),
    getFibonacciOverview: () =>
      server.get('/fibonacci/organization/transactions/dashboard/overview'),
    getFibonacciQuickCount: () =>
      server.get('/fibonacci/organization/dashboard/quick-count'),
    getFibonacciTransactionAnalytics: () =>
      server.get('/fibonacci/organization/transactions/overview'),
    getFibonacciTransactionChartData: (query: string) =>
      server.get(`/fibonacci/organization/transactions/graph-data/${query}`),
    transaction: {
      createTransactionNote: (
        data: TransactionNoteModel,
        transactionId: string
      ) =>
        server.post(`/fibonacci/add-transaction-note/${transactionId}`, data),
      approvedTransaction: (data: ApprovedModel, transactionId: string) =>
        server.post(`/fibonacci/approve-transaction/${transactionId}`, data),
      escalateTransaction: (data: EscalateModel, transactionId: string) =>
        server.post(`/fibonacci/escalate-transaction/${transactionId}`, data),
      escalateCase: (data: EscalateModel) =>
        server.post('/fibonacci/cases/escalate', data),
      assignCase: (data: IAssignCase) =>
        server.post('/fibonacci/cases/assign', data),
      getSingleTransaction: (id: string) =>
        server.get(`/fibonacci/transactions/${id}`),
      getTransactionNote: (id: string) =>
        server.get(`/fibonacci/transaction-notes/${id}`),
      getTransactionIssuer: (query?: string) =>
        server.get(`/fibonacci/transaction-issuers${query}`),
      getTransactions: (query?: string) =>
        server.get(`/fibonacci/transactions${query}`),
      getTransactionActivity: (query: string, id: string) =>
        server.get(`/fibonacci/transactions/${id}/activity${query}`),
    },
    getAiSession: () => server.get('/fibonacci/get-ai-session'),
    customers: {
      getAllCustomers: (query: string) =>
        server.get(`/fibonacci/customers${query}`),
      getCustomerDetails: (id: string) =>
        server.get(`/fibonacci/customer/${id}`),
      addCustomerToBlacklist: (customerId: string, action: 'add' | 'remove') =>
        server.post(
          `/fibonacci/update-customer-blacklist/${action}/${customerId}`
        ),
      addCustomerToWatchlist: (customerId: string, action: 'add' | 'remove') =>
        server.post(
          `/fibonacci/update-customer-watchlist/${action}/${customerId}`
        ),
      getCustomerDeviceTrail: (customerId: string, query: string) =>
        server.get(`/fibonacci/device-trail/${customerId}${query}`),
      getCustomerLocationTrail: (customerId: string, query: string) =>
        server.get(`/fibonacci/location-trail/${customerId}${query}`),
      getCustomerTransactionChart: (customerId: string, query: string) =>
        server.get(`/fibonacci/customer/${customerId}/metrics${query}`),
    },
    rules: {
      getPredefinedRules: (query?: string) =>
        server.get(`/fibonacci/predefined-rules${query}`),
      getRules: (query?: string) => server.get(`/fibonacci/rules${query}`),
      getRulesAnalytics: (id?: string) =>
        server.get(`/fibonacci/rules/${id}/analysis`),
      getTransactionsByRuleId: (id: string, query?: string) =>
        server.get(`/fibonacci/rules/${id}/transactions${query}`),
      getRuleGraph: (id: string, query?: string) =>
        server.get(`/fibonacci/rules/${id}/graph${query}`),
      getAutoSuggestionRule: () => server.get(`/fibonacci/auto-suggest-rules`),
      updateRule: (action: string) =>
        server.put(`/fibonacci/update-rule-state/${action}`),
      createRule: (data: RulesModel) =>
        server.post('/fibonacci/create-rule', data),
      testRule: (data: TestRuleModel) =>
        server.post('/fibonacci/test-rule', data),
      getFatfList: () => server.get(`/fibonacci/fatf-list`),
    },
    team: {
      getCaseManagers: () => server.get(`/fibonacci/get-case-managers`),
      addCaseManagers: (data: { userIds: string[] }) =>
        server.post('/fibonacci/add-case-managers', data),
      deleteCaseManager: (id: string) =>
        server.delete(`/fibonacci/remove-case-managers/${id}`),
      getTeamMember: (query?: string) =>
        server.get(`/fibonacci/team-members${query}`),
      updateTeamMember: (data: TeamMemberModel, id: string) =>
        server.put(`/fibonacci/update-team-member/${id}`, data),
      createTeamMember: (data: TeamMemberModel) =>
        server.post('/fibonacci/add-team-member', data),
      deleteTeamMember: (id: string) =>
        server.delete(`/fibonacci/remove-team-member/${id}`),
    },
    role: {
      getRoles: (query?: string) => server.get(`/fibonacci/roles${query}`),
      updateRole: (data: SettingsFRoleModel, id: string) =>
        server.put(`/fibonacci/update-role/${id}`, data),
      createRole: (data: SettingsFRoleModel) =>
        server.post('/fibonacci/create-role', data),
      deleteRole: (params: string) =>
        server.delete(`/fibonacci/delete-role/${params}`),
    },
    permissions: { getPermissions: () => server.get(`/fibonacci/permissions`) },
  },
  team: {
    getTeamMembers: (query?: string) =>
      serverExo.get(`/user/team-members${query}`),
    updateTeamMember: (data: TeamMemberModel, id: string) =>
      serverExo.put(`/user/update-team-member/${id}`, data),
    createTeamMember: (data: TeamMemberModel) =>
      serverExo.post('/user/add-team-member', data),
    deleteTeamMember: (id: string) =>
      serverExo.delete(`/user/delete-team-member/${id}`),
  },
  diana: {
    validateLoadCSV: (data: ValidateDianaOnboardingCSV) =>
      serverDiana.post('/validate-load-csv', data),
    validateTarrifCSV: (data: ValidateDianaOnboardingCSV) =>
      serverDiana.post('/validate-tariff-csv', data),
    updateWhatIf: (url: string) =>
      serverDiana.post(`/what-if-tax-credit${url}`),
    getDiana: () => server.get(`/diana/`),
    getAnalysis(type: string, projectId: string) {
      return serverDianaApi.get(
        `/get_specificRiskTab?project_id=${projectId}&tab=${type}`
      );
    },
    project: {
      getRecentDianaProjects: () =>
        serverDianaApi.get('/diana/projects/recent/energy'),
      getRecentCREProjects: (config?: AxiosRequestConfig) =>
        serverDianaApi.get('/diana/projects/recent/CRE', config),
      getDianaProject: () => serverDianaApi.get(`/diana/projects`),
      getDianaProjectsLite: () => serverDianaApi.get(`/v1/diana/projects/lite`),
      // getDianaProject: (userId: string) =>
      //   serverDianaApi.get(`/diana/projects?user_id=${userId}`),
      dianaOnboardingProjectAnalysis: ({
        projectId,
        projectType,
      }: {
        projectId: string;
        projectType?: string;
      }) => {
        const projectApiUrl =
          projectType === 'real-estate'
            ? `cre/v1/${projectId}/submit`
            : `v1/project/${projectId}/submit`;

        return serverDiana.post(projectApiUrl);
      },
      createDianaProject: (projectType?: string) =>
        serverDianaApi.post('/diana/project', {
          diana_project_type: projectType,
        }),
      createCREProject: (
        projectId: string,
        payload: FileCREProjectData | FileCREProjectPollingData
      ) =>
        serverCreApi.post(`${projectId}/extract-payload-from-files`, payload),
      retrieveLastMessage: (projectId: string) =>
        serverDianaApi.get(`/diana/${projectId}/retrieve-last-document-chat`),
      createDTOV2Project: (
        projectid: string,
        values: NewAnalysisInvestmentProjectData
      ) => serverCreApi.post(`${projectid}/prepare-dto`, values),
      checkDTOV2ProjectPayload: (
        projectid: string,
        values: NewAnalysisInvestmentProjectData
      ) => serverDiana.post(`/cre/v2/${projectid}/verify-payload`, values),
      checkDTOV3ProjectPayload: (
        projectid: string,
        values: NewAnalysisInvestmentProjectData
      ) => serverCreApi.post(`${projectid}/verify-payload`, values),
      startDTOV2ProjectAnalysis: (projectid: string) => {
        return serverDiana.post(`/cre/v3/${projectid}/submit`);
      },
      createDianaAnalyzedProject: ({
        values,
        url,
      }: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        values: any;
        url: string;
      }) => serverDiana.post(url, values),
      deleteProject: (project_id: string) =>
        serverDianaApi.delete(`/diana/project/${project_id}`),
      projectAnalysisStatus: (project_id: string) =>
        serverDianaApi.get(`/project/${project_id}/analysis/state`),
      projectCREAnalysisStatus: (project_id: string) =>
        serverDianaApi.get(`/project/${project_id}/analysis/v1/cre/state`),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getProjectReport: (project_id: any) =>
        serverDiana.post(`/v2/diana/project/report?project_id=${project_id}`),
      getProjectReportV3: (project_id: string, format: 'pdf' | 'docx') =>
        serverCreApi.post(`report/${project_id}/generate?format=${format}`),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getGenerateWorkBook: (project_id: any) =>
        serverCreApi.post(`workbook/${project_id}/generate`),
      prepareProjectDto: ({
        project_id,
        projectType,
      }: {
        project_id: string;
        projectType?: string;
      }) => {
        const projectApiUrl =
          projectType === 'real-estate'
            ? `cre/v1/${project_id}/prepare-dto`
            : `/v2/prepare-project-dto?project_id=${project_id}`;
        return serverDiana.post(projectApiUrl);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dervetPrechecker: (project: any) =>
        serverDiana.post(`/v2/dervet-prechecker`, project),
      getRecentAllProjects: () => serverDianaApi.get('/diana/projects/recent'),
      getAllRecentProjects: () =>
        serverDianaApi.get('/v2/diana/projects/recent'),
      autoFillCRE: (
        projectId: string,
        payload: NewAnalysisInvestmentProjectData,
        pageToFill:
          | 'about_property'
          | 'development_plan'
          | 'operating_assumptions'
          | 'investment_structure'
          | 'financing_structure'
      ) =>
        serverCreApi.post(
          `${projectId}/autofill-dto?current_page=${pageToFill}`,
          payload
        ),
      chatAutoFillCRE: (projectId: string, payload: CREAutoFillChatPayload) =>
        serverCreApi.post(`${projectId}/autofill-dto-message`, payload),
      getDianaDraft: (projectId: string) =>
        serverDianaApi.get(`diana/project/draft/${projectId}`),
      getIncompleteDraft: (projectId: string) =>
        serverDianaApi.get(`diana/project/details/${projectId}`),
      getAllDrafts: () => serverDianaApi.get(`/diana/project/draft-all`),
    },
    batch: {
      getAllBatchAnalysis: (query?: string) =>
        serverDianaApi.get(`/v1/batches${query}`),
      getAllBatchAnalysisDetail: (query?: string) =>
        serverDianaApi.get(`/v1/batch${query}`),
      getSingleBatchAnalysis: (id?: string) =>
        serverDianaApi.get(`/v1/batch/${id}`),
      getBatchOverview: () => serverDianaApi.get(`/analysis/stats`),
      createBatchAnalysis: (data: {
        batch_name: string;
        organization_id: string;
        projects: BatchProjectModelTest[];
      }) => serverDiana.post('/v1/batch-analysis', data),
      deleteBatchProject: ({
        batchId,
        projectId,
      }: {
        batchId: string;
        projectId: string;
      }) => serverDianaApi.delete(`/batch/${batchId}/project/${projectId}`),
      deleteBatch: (batchId: string) =>
        serverDianaApi.delete(`/batch/${batchId}`),
    },
    chat: {
      getDianaChatHistory: (projectId: string, projectType: string) => {
        if (projectType === 'real_estate') {
          return serverDiana.get(
            `/chat-history-real-estate?project_id=${projectId}`
          );
        }
        return serverDianaApi.get(`/diana/chat-history/${projectId}`);
      },
      getAskDianaChatHistory: (
        projectId?: string,
        section?: string,
        isExplore?: boolean
      ) => {
        if (isExplore)
          return serverDiana.get(
            `/diana-chat-history-scenario?project_id=${projectId}`
          );
        return serverDiana.get(
          `/diana-chat-history?project_id=${projectId}&section=${section}`
        );
      },
      getAskDianaChatRecommendation: (projectId: string) => {
        return serverCreApi.post(
          `/get_chat_recommendations?project_id=${projectId}`
        );
      },
      createDianaChat: ({
        projectId,
        value,
        projectType,
      }: {
        projectId?: string;
        value?: OnboardingDianaChatItem;
        projectType?: string | null;
      }) => {
        const payload = value ? { ...value } : null;
        const projectApiUrl =
          projectType === 'real-estate'
            ? `/cre/project_onboarding_conversation/${projectId}`
            : `/project/${projectId}/onboarding/conversation`;
        return serverDianaApi.post(projectApiUrl, payload);
      },
      createAskDianaChat: ({ value }: { value: DianaMessageMode }) => {
        return serverCreApi.post(`/get_chat_response`, value);
      },
      createScenariosChat: ({ value }: { value: ScenariosMessageMode }) => {
        return serverDiana.post(`/get_chat_response-scenario`, value);
      },
    },
    configuration: {
      getConfiguration: (url: string) =>
        serverDiana.post(`/get-diana-configurations${url}`),
      resetConfiguration: (url: string) =>
        serverDiana.post(`/diana-reset-configuration${url}`),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      updateConfiguration: (data: any) =>
        serverDiana.post(`/diana-edit-configuration`, data),
    },
    cre: {
      getDocCheckerCategories: () =>
        serverCreApi.get(`document-analysis/categories`),
      analyzeDoc: (values: docCheckerAnalysis) =>
        serverDiana.post(`v1/document-analysis/analyze-multiple`, values),
      saveDraft: (
        projectId: string,
        values: NewAnalysisInvestmentProjectData
      ) =>
        serverDianaApi.post(`/diana/project/draft?project_id=${projectId}`, {
          data: values,
        }),
      deleteDraft: (projectId: string) =>
        serverDianaApi.delete(`diana/project/draft-delete/${projectId}`),
      deleteProject: (projectId: string) =>
        serverDianaApi.delete(`diana/project/${projectId}`),
      renameDraft: (projectId: string, payload: { new_name: string }) =>
        serverDianaApi.post(
          `diana/project/draft-rename?project_id=${projectId}`,
          payload
        ),
      renameProject: (projectId: string, payload: { new_name: string }) =>
        serverDianaApi.post(
          `diana/project/cre/rename?project_id=${projectId}`,
          payload
        ),
      duplicateProject: (projectId: string) =>
        serverDianaApi.post(`diana/project/cre/duplicate/${projectId}`),
      getAnalyzedDocs: (projectId: string, params?: GetAnalyzedDocsParams) =>
        serverDianaApi.get<analyzedDocs>(
          `/diana/document-analysis/analyses/${projectId}`,
          {
            params,
            paramsSerializer: (params) =>
              qs.stringify(params, { arrayFormat: 'repeat' }),
          }
        ),
      deleteIssue: (
        projectId: string,
        payload: { deletions: { analysis_id: string; issue_id: string }[] }
      ) => {
        const url = `/diana/document-analysis/issues/delete?project_id=${projectId}`;
        // return serverDiana.request({
        //   url: `/v1/document-analysis/issues/delete?project_id=${projectId}`,
        //   method: 'DELETE',
        //   data: payload,
        // });
        return serverDianaApi.post(url, {
          // data: {
          ...payload,
          // },
        });
      },
      resolveIssue: (
        projectId: string,
        payload: {
          updates: {
            analysis_id: string;
            issue_id: string;
            resolved: boolean;
            resolution_notes: string;
          }[];
        }
      ) => {
        const url = `diana/document-analysis/issues/resolve?project_id=${projectId}`;
        return serverDianaApi.post(url, {
          ...payload,
        });
      },

      deleteAnalysis: (analysisId: string, projectId: string) => {
        const url = `diana/document-analysis/analyses/delete/${analysisId}?project_id=${projectId}`;
        return serverDianaApi.post(url);
      },
      getDocCheckerTags: (projectId: string) =>
        serverDianaApi.get<projectTags>(
          `/diana/document-analysis/tags/${projectId}`
        ),
      getInputAnalysis: (projectId: string) =>
        serverDianaApi.get(`/get_inputs_assumptions?project_id=${projectId}`),
      getChatRecommendations: (projectId: string) =>
        serverCreApi.post(`/get_chat_recommendations?project_id=${projectId}`),
      getUserStats: () => serverDianaApi.get('/get-user-stats'),
      inviteInvestor: (payload: inviteInvestorPayload) =>
        serverDianaApi.post(`/cre/invites`, payload),
      getInvitedInvestors: (
        projectId: string,
        params?: Record<string, string | number | boolean | undefined>
      ) => {
        const query = new URLSearchParams();
        Object.entries(params || {}).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            query.append(key, String(value));
          }
        });
        const url = `/cre/invites/project/${projectId}${
          query.toString() ? `?${query.toString()}` : ''
        }`;
        return serverDianaApi.get(url);
      },
      getInvestorQuestions: (
        projectId: string,
        params?: Record<string, string | number | boolean | undefined>
      ) =>
        serverDianaApi.get(
          `cre/invites/project/all/conversations/${projectId}`,
          {
            params,
          }
        ),
      getStakeholderConversations: (
        projectId: string,
        stakeholderId: string,
        size: number
      ) =>
        serverDianaApi.get(
          `/diana/retrieve-stakeholder-chats?project_id=${projectId}&stakeholder_id=${stakeholderId}&size=${size}`
        ),
      getInvestorQuestionsV2: (projectId: string) =>
        serverDianaApi.get(
          `diana/retrieve-project-stakeholders-chat?project_id=${projectId}`
        ),
      getInterestScoring: (projectId: string) =>
        serverDianaApi.get(`cre/invites/project/interest-score/${projectId}`),
      getConversionFunnel: (projectId: string) =>
        serverDianaApi.get(`/cre/invites/project/conversion-rate/${projectId}`),
      getQuestionAnalytics: (projectId: string) =>
        serverDianaApi.get(
          `cre/invites/project/questions-analytics/${projectId}`
        ),
      updateInviteeRole: (payload: updateInviteePayload) =>
        serverDianaApi.put(`cre/invites/role`, payload),
      revokeInvite: (payload: deleteInviteePayload) =>
        serverDianaApi.post(`cre/invites/revoke`, payload),
      resendInvite: (payload: deleteInviteePayload) =>
        serverDianaApi.post(`cre/invites/resend`, payload),
      updateInviteeName: (payload: updateInviteeNamePayload) =>
        serverDianaApi.put(`cre/invites/user`, payload),

      deleteInvitee: (payload: deleteInviteePayload) =>
        serverDianaApi.delete(`/cre/invites`, { data: payload }),
      validateInvitee: (token: string) =>
        serverDianaApi.get(`cre/invites/validate/${token}`),
      getInviteeOverview: () =>
        serverDianaApi.get(`cre/invites/project-overview/`),
      requestNewLink: (payload: requestNewLinkPayload) =>
        serverDianaApi.post(`cre/invites/request-link`, payload),
      getInteractiveReport: (projectId: string) =>
        serverDianaApi.get(`cre/invites/project-report-data/${projectId}`),
      removeInviteProjectComment: (commentId: string) =>
        serverDianaApi.delete(
          `cre/invites/project/comments/remove/${commentId}`
        ),
      submitProjectReaction: (payload: {
        action: 'interested' | 'not_interested';
      }) => serverDianaApi.post(`/cre/invites/project/reactions`, payload),
      editInput: (projectId: string, values: EditInputsParams) =>
        serverDianaApi.post(
          `/edit_inputs_assumptions?project_id=${projectId}`,
          values
          // {
          //   data: values,
          // }
        ),
      rerun: (projectId: string) =>
        serverDiana.post(`/cre/v3/${projectId}/rerun`),
    },
  },
  common: {
    getCommonProducts: () => server.get(`/common/products`),
    requestDemo: (data: FormRequestDemoValue) =>
      server.post('/common/request-demo', data),
    verifyEmail: (data: { email: string }) =>
      server.post('/common/verify-user-email', data),
    uploadFile: (file: FormData) => server.post('/common/upload', file),
  },
  subscriptions: {
    getSubscriptionPlans: (agent: string) =>
      serverExo.get(`/subscriptions/plans/${agent}`),
    getActiveUserSubscription: () =>
      serverExo.get(`/subscriptions/active/plan`),
    initialisePayment: (data: { plan_id: string }) =>
      serverExo.post('/payments/initialize', data),
  },
  screening: {
    getSettings: () => server.get('/screening/settings'),
    updateSettings: (id: string, data: UpdateScreeningSettingsModel) =>
      server.put(`/screening/settings/${id}`, data),
    screeningResults: (customerId: string, query: string) =>
      server.get(`/screening/results/${customerId}${query}`),
    getScreeningResult: (customerId: string, resultId: string) =>
      server.get(`/screening/results/${customerId}/${resultId}`),
    runCheck: () => server.post('/screening/organization'),
  },
  reports: {
    generateReport: (data: GenerateReportModel) =>
      server.post(
        `/report/generate/${data?.reportType}/${data?.documentType}`,
        data?.data
      ),
  },
  euclid: {
    getFileUrl: async (bankStatement: File | null) => {
      if (!bankStatement) {
        throw new Error('No file selected for upload');
      }

      const formData = new FormData();
      formData.append('file', bankStatement);

      try {
        const response = await serverDianaApi.post(
          '/v1/credit-analyis/upload-file',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    uploadCreditAnalysisRequest: (
      creditAnalysisForm: EuclidCreditAnalysisSchema
    ) => {
      try {
        const response = serverDianaApi.post(
          '/v1/credit-analysis/submit-request',
          creditAnalysisForm
        );
        return response;
      } catch (error) {
        throw error;
      }
    },
  },
  cre: {
    getUploadUrl: (filename: string) =>
      serverDianaApi.get(`/cre/invites/upload-url`, {
        params: {
          filename,
        },
      }),
    bulkInviteUser: (data: BulkInviteUserPayload) =>
      serverDianaApi.post('/cre/invites/bulk', data),
  },
};

export default apis;
